// Three-region layout for every authenticated route. Children render
// inside the main content area; sidebar + top bar are static chrome.
//
// Layout:
//
//   ┌─────────────────────┬───────────────────────────────────────┐
//   │ Sidebar (240px)     │ TopBar (h-14)                         │
//   │                     ├───────────────────────────────────────┤
//   │                     │ description row (subtitle, optional)  │
//   │                     ├───────────────────────────────────────┤
//   │                     │ Main content (children)               │
//   │                     │                                       │
//   └─────────────────────┴───────────────────────────────────────┘
//
// Mobile (<768px): sidebar collapses behind a slide-out. Hamburger
// in the top bar toggles. Backdrop overlay closes on tap. Below
// `md`, the main column spans the full width.

import { useState, type ReactNode } from 'react';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { usePageHeader } from './PageHeaderContext';

// Slim mobile-only strip used when the page opts out of the top bar
// but we still need the hamburger to be reachable below `md`.
function MobileMenuStrip({ onMenuToggle }: { onMenuToggle: () => void }): JSX.Element {
  return (
    <div className="flex h-10 items-center border-b border-border bg-bg px-2 md:hidden">
      <button
        type="button"
        onClick={onMenuToggle}
        aria-label="Toggle navigation"
        className="-ml-1 inline-flex h-9 w-9 items-center justify-center rounded-md text-ink-2 hover:bg-bg-soft"
      >
        <span aria-hidden="true" className="text-lg leading-none">
          ☰
        </span>
      </button>
    </div>
  );
}

type Props = {
  children: ReactNode;
};

export function AppShell({ children }: Props): JSX.Element {
  const [mobileOpen, setMobileOpen] = useState(false);
  const header = usePageHeader();
  const closeMobile = (): void => setMobileOpen(false);
  const toggleMobile = (): void => setMobileOpen((v) => !v);

  return (
    <div className="flex min-h-screen bg-bg-soft">
      {/* Desktop sidebar: static column in the layout. */}
      <div className="hidden md:flex md:sticky md:top-0 md:h-screen">
        <Sidebar />
      </div>

      {/* Mobile sidebar: fixed slide-out + backdrop. Mounted always
          (display:hidden when closed) so the slide animation is
          tractable without orchestrating mount/unmount. */}
      {mobileOpen ? (
        <div
          aria-hidden="true"
          onClick={closeMobile}
          className="fixed inset-0 z-30 bg-black/40 md:hidden"
        />
      ) : null}
      <div
        className={`fixed inset-y-0 left-0 z-40 transition-transform md:hidden ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <Sidebar onNavigate={closeMobile} />
      </div>

      <div className="flex min-w-0 flex-1 flex-col">
        {header?.hideTopBar ? (
          // Pages with their own headline opt out of the top bar to
          // avoid a double-label. We still render a slim mobile-only
          // strip so the hamburger toggle is reachable below `md`.
          <MobileMenuStrip onMenuToggle={toggleMobile} />
        ) : (
          <TopBar onMenuToggle={toggleMobile} />
        )}
        {header?.subtitle && !header?.hideTopBar ? (
          <div className="border-b border-border bg-bg px-4 py-2 text-sm text-ink-2 sm:px-6">
            {header.subtitle}
          </div>
        ) : null}
        <main className="flex-1">
          {/* Content container. 1600px cap (was 1280px) uses more of
              wide monitors — Patient Detail, Orders, Reports all
              benefit from the extra room for tables/cards. Dispatch
              opts out of this container by using hideTopBar +
              its own full-viewport layout, so it isn't affected. */}
          <div className="mx-auto w-full max-w-[1600px] px-4 py-3 sm:px-6">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
